import React from 'react';
import { useNavigate } from 'react-router-dom';

// Source: Masjid Ghausia Rotterdam — publicly published monthly prayer times
// https://masjidghausia.nl/gebedstijden/
const MASJID = {
  name: 'Masjid Ghausia',
  city: 'Rotterdam',
  homepage: 'https://masjidghausia.nl/gebedstijden/',
};

const MONTHS = [
  { key: 1,  name: 'January',   nl: 'Januari',   pdf: 'https://masjidghausia.nl/wp-content/uploads/2026/07/1-Jan-.pdf' },
  { key: 2,  name: 'February',  nl: 'Februari',  pdf: 'https://masjidghausia.nl/wp-content/uploads/2026/07/2-feb-.pdf' },
  { key: 3,  name: 'March',     nl: 'Maart',     pdf: 'https://masjidghausia.nl/wp-content/uploads/2026/07/3-Mrt.pdf' },
  { key: 4,  name: 'April',     nl: 'April',     pdf: 'https://masjidghausia.nl/wp-content/uploads/2026/07/4-Apr.pdf' },
  { key: 5,  name: 'May',       nl: 'Mei',       pdf: 'https://masjidghausia.nl/wp-content/uploads/2026/07/5-Mei.pdf' },
  { key: 6,  name: 'June',      nl: 'Juni',      pdf: 'https://masjidghausia.nl/wp-content/uploads/2026/07/6-jun.pdf' },
  { key: 7,  name: 'July',      nl: 'Juli',      pdf: 'https://masjidghausia.nl/wp-content/uploads/2026/01/Jul.pdf' },
  { key: 8,  name: 'August',    nl: 'Augustus',  pdf: 'https://masjidghausia.nl/wp-content/uploads/2026/01/Aug.pdf' },
  { key: 9,  name: 'September', nl: 'September', pdf: 'https://masjidghausia.nl/wp-content/uploads/2026/01/Sep.pdf' },
  { key: 10, name: 'October',   nl: 'Oktober',   pdf: 'https://masjidghausia.nl/wp-content/uploads/2026/01/Okt.pdf' },
  { key: 11, name: 'November',  nl: 'November',  pdf: 'https://masjidghausia.nl/wp-content/uploads/2026/01/Nov.pdf' },
  { key: 12, name: 'December',  nl: 'December',  pdf: 'https://masjidghausia.nl/wp-content/uploads/2026/01/Dec.pdf' },
];

const RAMADAN = {
  name: 'Ramadan',
  nl: 'Ramadan',
  pdf: 'https://masjidghausia.nl/wp-content/uploads/2026/07/Ramdan-.pdf',
};

const PrayerTimes = () => {
  const navigate = useNavigate();
  const currentMonth = new Date().getMonth() + 1;

  const openPdf = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const printPdf = (url) => {
    // Open the PDF in a new tab; browser's PDF viewer has a Print button.
    // We also try to auto-trigger print after load — some browsers block cross-origin iframes for PDFs,
    // in which case the tab simply shows the PDF and the user can hit Cmd/Ctrl+P.
    const w = window.open(url, '_blank', 'noopener,noreferrer');
    if (w) {
      // Best-effort auto-print
      w.addEventListener?.('load', () => { try { w.focus(); w.print(); } catch { /* silent */ } });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/staff')} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all shadow-md border border-gray-200" data-testid="back-to-admin-btn">
              <i className="fas fa-arrow-left"></i>
              <span>Back to Admin</span>
            </button>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-teal-600">Gebedstijden</p>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">Prayer Times</h1>
              <p className="text-xs text-slate-500 mt-1">
                Live from <a href={MASJID.homepage} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline font-semibold">{MASJID.name}, {MASJID.city}</a>
              </p>
            </div>
          </div>
        </div>

        {/* Ramadan featured card */}
        <div className="bg-gradient-to-br from-purple-700 via-indigo-800 to-slate-900 text-white rounded-2xl p-5 shadow-xl mb-6 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="ramadan-card">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center">
              <i className="fas fa-star-and-crescent text-3xl text-amber-300"></i>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-amber-300">Special schedule</p>
              <h2 className="text-2xl font-black">Ramadan</h2>
              <p className="text-sm text-white/70">Suhur &amp; Iftar timings for the whole holy month</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => openPdf(RAMADAN.pdf)}
              className="px-4 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl font-semibold text-sm transition-all backdrop-blur"
              data-testid="ramadan-view-btn"
            >
              <i className="fas fa-eye mr-1"></i> View
            </button>
            <button
              onClick={() => printPdf(RAMADAN.pdf)}
              className="px-4 py-2.5 bg-amber-400 hover:bg-amber-500 text-amber-950 rounded-xl font-bold text-sm shadow-lg"
              data-testid="ramadan-print-btn"
            >
              <i className="fas fa-print mr-1"></i> Print
            </button>
          </div>
        </div>

        {/* 12 monthly cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="months-grid">
          {MONTHS.map((m) => {
            const isCurrent = m.key === currentMonth;
            return (
              <div
                key={m.key}
                className={`bg-white rounded-2xl shadow-md border p-4 transition-all hover:shadow-lg ${isCurrent ? 'border-teal-500 ring-2 ring-teal-100' : 'border-slate-200'}`}
                data-testid={`month-card-${m.key}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className={`text-[10px] font-bold uppercase tracking-[0.25em] ${isCurrent ? 'text-teal-600' : 'text-slate-400'}`}>
                      {String(m.key).padStart(2, '0')}
                      {isCurrent && <span className="ml-2 inline-block bg-teal-500 text-white px-1.5 py-0.5 rounded text-[8px]">NOW</span>}
                    </p>
                    <h3 className="text-lg font-black text-slate-900 leading-tight mt-0.5">{m.name}</h3>
                    <p className="text-[11px] text-slate-400">{m.nl}</p>
                  </div>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCurrent ? 'bg-teal-100' : 'bg-slate-100'}`}>
                    <i className={`fas fa-mosque ${isCurrent ? 'text-teal-600' : 'text-slate-500'}`}></i>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => openPdf(m.pdf)}
                    className="flex-1 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition-all"
                    data-testid={`view-${m.key}`}
                  >
                    <i className="fas fa-eye mr-1"></i> View
                  </button>
                  <button
                    onClick={() => printPdf(m.pdf)}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold text-white transition-all ${isCurrent ? 'bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700' : 'bg-slate-700 hover:bg-slate-800'}`}
                    data-testid={`print-${m.key}`}
                  >
                    <i className="fas fa-print mr-1"></i> Print
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-[11px] text-slate-500 mt-6 leading-relaxed text-center">
          <i className="fas fa-info-circle mr-1"></i>
          Prayer time PDFs are published by <a href={MASJID.homepage} target="_blank" rel="noopener noreferrer" className="underline">Masjid Ghausia</a>. Clicking <strong>Print</strong> opens the PDF in a new tab and starts the print dialog; if it does not, press <kbd className="px-1 rounded bg-slate-200">Ctrl</kbd> + <kbd className="px-1 rounded bg-slate-200">P</kbd> or <kbd className="px-1 rounded bg-slate-200">⌘</kbd> + <kbd className="px-1 rounded bg-slate-200">P</kbd> from the PDF page.
        </p>
      </div>
    </div>
  );
};

export default PrayerTimes;
